/**
 * 成功時のエフェクトコンポーネント
 * type: 'star' | 'confetti' | 'shine'
 *
 * 2026-06-12 R1: hex 直書きを mood トークン（var(--mg-*)）へ置換（色のみ・
 * パーティクル挙動/本数/タイミング/API は不変）。濃淡は color-mix で作る。
 */
import { useEffect, useState, useCallback } from 'react';

const PARTICLE_COUNT = 30;
// 旧 amber 系 hex（#fbbf24/#fcd34d/#fef08a/#ffffff）→ success を fg 側へ段階的に明るくする
const STAR_COLORS = [
  'var(--mg-success)',
  'color-mix(in srgb, var(--mg-success) 75%, var(--mg-fg))',
  'color-mix(in srgb, var(--mg-success) 50%, var(--mg-fg))',
  'var(--mg-fg)',
];
// 旧レインボー hex（red/orange/yellow/green/blue/purple/pink）→ 利用可能トークンと
// その混色で同等の色幅を再現する（mood 切替に追従）
const CONFETTI_COLORS = [
  'var(--mg-error)',
  'color-mix(in srgb, var(--mg-error) 55%, var(--mg-hint))',
  'var(--mg-hint)',
  'var(--mg-success)',
  'var(--mg-accent)',
  'color-mix(in srgb, var(--mg-accent) 55%, var(--mg-error))',
  'color-mix(in srgb, var(--mg-error) 60%, var(--mg-fg))',
];

function generateParticles(count, colors) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 50 + (Math.random() - 0.5) * 30,
    y: 50 + (Math.random() - 0.5) * 30,
    size: Math.random() * 8 + 4,
    color: colors[Math.floor(Math.random() * colors.length)],
    angle: Math.random() * 360,
    speed: Math.random() * 80 + 40,
    delay: Math.random() * 0.3,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 720
  }));
}

export function SuccessEffect({ type = 'star', duration = 1500, onComplete }) {
  const [particles, setParticles] = useState([]);
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const colors = type === 'confetti' ? CONFETTI_COLORS : STAR_COLORS;
    setParticles(generateParticles(PARTICLE_COUNT, colors));

    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);

      if (p < 1) {
        requestAnimationFrame(animate);
      } else {
        setVisible(false);
        onComplete?.();
      }
    };
    requestAnimationFrame(animate);
  }, [type, duration, onComplete]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 9999
      }}
    >
      {/* 中央のフラッシュ効果 */}
      {type === 'shine' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `${100 + progress * 200}px`,
            height: `${100 + progress * 200}px`,
            borderRadius: '50%',
            background: `radial-gradient(circle, color-mix(in srgb, var(--mg-success) ${60 * (1 - progress)}%, transparent) 0%, transparent 70%)`,
            animation: 'pulse 0.3s ease-out'
          }}
        />
      )}

      {/* パーティクル */}
      {particles.map((p) => {
        const t = Math.max(0, progress - p.delay) / (1 - p.delay);
        const eased = 1 - Math.pow(1 - t, 3);
        const distance = eased * p.speed;
        const radians = (p.angle * Math.PI) / 180;
        const x = p.x + Math.cos(radians) * distance;
        const y = p.y + Math.sin(radians) * distance + eased * eased * 30;
        const opacity = 1 - Math.pow(t, 2);
        const scale = 1 - t * 0.5;
        const rotation = p.rotation + t * p.rotationSpeed;

        return (
          <div
            key={p.id}
            style={{
              position: 'absolute',
              left: `${x}%`,
              top: `${y}%`,
              width: `${p.size * scale}px`,
              height: `${p.size * scale}px`,
              opacity,
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              transition: 'none'
            }}
          >
            {type === 'star' ? (
              // fill は style 経由で渡す（var()/color-mix は SVG の presentation 属性では
              // 解釈されないため。描画結果は不変）
              <svg viewBox="0 0 24 24" style={{ fill: p.color }} width="100%" height="100%">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: p.color,
                  borderRadius: type === 'confetti' ? '2px' : '50%'
                }}
              />
            )}
          </div>
        );
      })}

      {/* 完了テキスト */}
      {type !== 'shine' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) scale(${1 + (1 - progress) * 0.5})`,
            fontSize: '2rem',
            fontWeight: 'bold',
            color: 'var(--mg-success)',
            textShadow: '0 2px 8px color-mix(in srgb, var(--mg-bg) 50%, transparent)',
            opacity: 1 - progress
          }}
        >
          ✓
        </div>
      )}
    </div>
  );
}

export default SuccessEffect;
